<template>
  <div class="embed">
    <div v-if="loadingEntity" class="lb-loading">Chargement...</div>
    <p v-else-if="!entity" class="lb-loading">{{ level === 'ligue' ? 'Ligue introuvable.' : 'Comité introuvable.' }}</p>

    <template v-else>
      <div class="embed-header">
        <img v-if="logoUrl(entity.logo)" :src="logoUrl(entity.logo)!" :alt="`Logo ${entity.name}`"
          class="embed-logo" />
        <h2 class="embed-title">Classement général {{ entity.name }} {{ seasonLabel }}</h2>
      </div>

      <!-- Affiché sous 768px de large (styles.css) : largeur de l'iframe, donc aussi dans
      une colonne étroite du site hôte. -->
      <div class="mobile-rotate-notice">
        <span>📱</span> Tourne ton téléphone pour voir toutes les colonnes
      </div>

      <div v-if="loading" class="lb-loading">Chargement du classement...</div>
      <div v-else-if="error" class="error">{{ error }}</div>
      <div v-else-if="!hasRankingEntries(leaderboard)" class="lb-loading">
        Aucun classement disponible pour la saison {{ seasonLabel }}.
      </div>
      <PublicRankingTables v-else :categories="leaderboard" :columns="columns" />

      <!-- Source, discrète en pied de page : le site qui intègre ce classement n'en est pas
      l'auteur, et la version complète (saisons, filtres, clubs) demande un compte DartsHub. -->
      <div class="embed-footer">
        <PoweredByDartsHub :href="fullUrl" :light="lightTheme" />
        <p class="embed-footer-note">
          Classement officiel mis à jour après chaque tournoi.
          Connectez-vous gratuitement pour voir les saisons précédentes, filtrer par catégorie
          et par {{ level === 'comite' ? 'club' : 'comité' }}, rechercher un joueur et retrouver vos résultats :
          <a :href="fullUrl" target="_blank" rel="noopener">voir le classement complet
            <i class="fa-solid fa-arrow-up-right-from-square"></i></a>
        </p>
      </div>
    </template>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute } from 'vue-router';
import backendApi from '@/axios/backendApi';
import { app_url } from '@/config/config';
import {
  EMBED_HEIGHT_MESSAGE, fetchComites, fetchLigues, fetchSeasons, hasRankingEntries, logoUrl, rankingColumns, sameCode,
  type Jurisdiction, type LeaderboardLevel, type RankingCategory,
} from '@/functions/leaderboards';
import PublicRankingTables from '@/components/PublicRankingTables.vue';
import { useThemeStore } from '@/stores/themeStore';
import PoweredByDartsHub from '@/components/PoweredByDartsHub.vue';

// Classement général en iframe sur un site tiers (WordPress d'un comité, d'une ligue...) :
// /embed/classement/national, /embed/classement/:ligueCode/ligue,
// /embed/classement/:ligueCode/:comiteCode. Toujours la version anonyme (saison en cours,
// sans code club/comité), même si le visiteur a une session DartsHub : le site hôte
// affiche la même chose à tout le monde. App.vue n'affiche ni en-tête ni pied de page
// sur ces routes (meta.embed). Le code à coller est généré par LeaderboardScope.
const route = useRoute();

// Thème choisi dans le code d'intégration, appliqué dès le setup (pas de flash sombre).
// Le fond est posé sur <html> : hors de .app-shell, rien d'autre ne le porte.
const lightTheme = route.query.theme === 'clair';
useThemeStore().setTheme(lightTheme ? 'calendrier-light' : 'calendrier-dark');
document.documentElement.style.background = 'var(--color-bg)';

const ligueCodeParam = computed(() => String(route.params.ligueCode ?? ''));
const comiteCodeParam = computed(() => (route.params.comiteCode ? String(route.params.comiteCode) : ''));
const level = computed<LeaderboardLevel>(() => {
  if (route.name === 'EmbedLeaderboardFederal') return 'federal';
  return comiteCodeParam.value ? 'comite' : 'ligue';
});
// Page complète correspondante : même chemin sans le préfixe /embed (ni ?theme).
const fullUrl = computed(() => `${app_url}${route.path.replace(/^\/embed/, '')}`);

const entity = ref<Jurisdiction | null>(null);
const loadingEntity = ref(true);
const seasonLabel = ref('');
const leaderboard = ref<RankingCategory[]>([]);
const tournaments = ref<Record<string, { place: string; date: string }>>({});
const loading = ref(false);
const error = ref('');

const columns = computed(() => rankingColumns(leaderboard.value, tournaments.value, level.value));

const fetchLeaderboard = async (seasonId: number) => {
  if (!entity.value) return;
  loading.value = true;
  error.value = '';
  try {
    const url = level.value === 'federal'
      ? `/leaderboard/federal/${seasonId}`
      : `/leaderboard/${level.value}/${entity.value.id}/${seasonId}`;
    const { data } = await backendApi.get(url);
    leaderboard.value = data.leaderboard || [];
    tournaments.value = data.tournaments || {};
  } catch (err: any) {
    error.value = err.response?.data?.detail || 'Échec du chargement du classement.';
  } finally {
    loading.value = false;
  }
};

// Hauteur envoyée à la page hôte (script fourni avec le code iframe) : l'iframe s'adapte
// au contenu au lieu d'afficher une barre de défilement.
let resizeObserver: ResizeObserver | null = null;
const postHeight = () => {
  if (window.parent === window) return;
  window.parent.postMessage({ type: EMBED_HEIGHT_MESSAGE, height: document.documentElement.scrollHeight }, '*');
};

onMounted(async () => {
  resizeObserver = new ResizeObserver(postHeight);
  resizeObserver.observe(document.body);
  let seasonId: number | null = null;
  try {
    const [ligues, seasons] = await Promise.all([fetchLigues(), fetchSeasons()]);
    const current = seasons.find((s) => s.is_current) ?? seasons[0];
    seasonId = current?.id ?? null;
    seasonLabel.value = current?.label ?? '';
    if (level.value === 'federal') {
      entity.value = { id: 0, code: 'FFD', name: 'National FFD', logo: null };
    } else {
      const ligue = ligues.find((l) => sameCode(l.code, ligueCodeParam.value)) ?? null;
      if (ligue && level.value === 'ligue') entity.value = ligue;
      if (ligue && level.value === 'comite') {
        const comites = await fetchComites(ligue.code);
        entity.value = comites.find((c) => sameCode(c.code, comiteCodeParam.value)) ?? null;
      }
    }
    if (entity.value) document.title = `Classement ${entity.value.name} | DartsHub`;
  } catch {
    entity.value = null;
  } finally {
    loadingEntity.value = false;
  }
  if (entity.value && seasonId != null) await fetchLeaderboard(seasonId);
  await nextTick();
  postHeight();
});

onBeforeUnmount(() => resizeObserver?.disconnect());
</script>

<style scoped>
.embed {
  padding: 1rem;
  box-sizing: border-box;
  background: var(--color-bg);
  color: var(--color-fg);
  font-family: var(--font-main);
}

.embed-header {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
  text-align: center;
}

.embed-logo {
  width: 56px;
  height: 56px;
  object-fit: contain;
}

.embed-title {
  margin: 0;
}

.embed-footer {
  margin-top: 1.25rem;
  text-align: center;
  font-size: 0.8rem;
}

.embed-footer a {
  color: var(--color-accent);
  text-decoration: none;
}

.embed-footer-note {
  max-width: 640px;
  margin: 0.4rem auto 0;
  font-size: 0.75rem;
  color: var(--color-fg-darker);
}
</style>
